import { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, ChevronDown as Chevron, Upload, X } from 'lucide-react';
import { createMediaBlobUrl } from '@/utils/blobMediaType';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { normalizeCta } from '../../utils';
import type { FieldIssue } from '../../types';
import type { Campaign, ValidationFeedback, AssetOrderToken } from '@/api/campaigns';
import { FieldLabel, InlineIssue, Field, inputClass, issueBorder, mergeIssues } from './shared';
import { GearExchangeModal, type GXImportedFile } from '../gear-exchange/GearExchangeModal';
import { GearExchangeImportButton } from '../gear-exchange/GearExchangeImportButton';
import { seedSignature } from './seedSignature';

/** Build the display-order tokens for a DC section from its ordered items. */
function dcOrderTokens(items: DCItem[]): AssetOrderToken[] {
  let newIdx = 0;
  return items.map((i) =>
    i.kind === 'existing' ? { existingAssetId: i.existingAssetId } : { newFileIndex: newIdx++ },
  );
}

/**
 * Adapt a DCItem to the shape seedSignature expects. Existing items carry the
 * stable asset id; locally-added files have no server identity yet and so
 * contribute nothing (they are never present in a freshly resolved list).
 */
function dcSeedKey(item: DCItem): { existingAssetId?: string; imageUrl?: string } {
  return item.kind === 'existing'
    ? { existingAssetId: item.existingAssetId, imageUrl: item.url }
    : {};
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_ASSETS = 10;
const MAX_VARIATIONS = 5;

const PRIMARY_TEXT_LIMIT = 125;
const HEADLINE_LIMIT = 40;
const DESCRIPTION_LIMIT = 25;

const CTA_OPTIONS = [
  'Apply Now',
  'Book Now',
  'Contact Us',
  'Get Offer',
  'Get Quote',
  'Learn More',
  'Order Now',
  'Shop Now',
  'Sign Up',
];

// ─── DCItem — represents either an existing DB asset or a pending new upload ──

export type DCItem =
  | { kind: 'existing'; url: string; existingAssetId: string; contentType?: string | null }
  | { kind: 'new'; file: File };

// ─── Initial values passed in from parent after API load ──────────────────────

export interface MetaDCInitialValues {
  primaryTexts?: string[];
  headlines?: string[];
  descriptions?: string[];
  cta?: string[];
  url?: string;
  legal?: string;
  initial1x1?: DCItem[];
  initial4x5?: DCItem[];
  initial9x16?: DCItem[];
}

/**
 * The ratios Dynamic Creative uploads into.
 *
 * Unlike the carousels, DC has no card pairing a 1:1 with a 4:5 — Meta mixes a
 * flat pool of assets against a flat pool of copy. So the three ratios are three
 * independent sections with independent limits, and feedCardSlots' paired-slot
 * model deliberately does not apply here.
 */
type DCRatio = '1x1' | '4x5' | '9x16';

/** Tailwind aspect class for a ratio's thumbnails and drop zone. */
function aspectClass(ratio: DCRatio): string {
  if (ratio === '9x16') return 'aspect-[9/16]';
  if (ratio === '4x5') return 'aspect-[4/5]';
  return 'aspect-square';
}

// ─── Video first-frame extractors ─────────────────────────────────────────────

function extractVideoFirstFrame(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    video.preload = 'metadata';
    video.muted = true;
    video.src = url;
    video.currentTime = 0.1;
    video.addEventListener('seeked', () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d')!.drawImage(video, 0, 0);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    });
    video.addEventListener('error', () => {
      URL.revokeObjectURL(url);
      reject(new Error('Video load failed'));
    });
    video.load();
  });
}

function extractVideoFirstFrameFromUrl(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.crossOrigin = 'anonymous';
    video.src = url;
    video.currentTime = 0.1;
    video.addEventListener('seeked', () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d')!.drawImage(video, 0, 0);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    });
    video.addEventListener('error', () => reject(new Error('Video load failed')));
    video.load();
  });
}

// ─── Asset thumbnail ──────────────────────────────────────────────────────────

function AssetThumb({
  item,
  index,
  isActive,
  isSectionActive,
  ratio,
  onClick,
  onRemove,
}: {
  item: DCItem;
  index: number;
  isActive: boolean;
  isSectionActive: boolean;
  ratio: DCRatio;
  onClick: () => void;
  onRemove: () => void;
}) {
  const [thumbUrl, setThumbUrl] = useState<string | undefined>(
    item.kind === 'existing' ? item.url : undefined,
  );

  useEffect(() => {
    if (item.kind === 'existing') {
      const isVideo = item.contentType?.startsWith('video/');
      if (isVideo) {
        extractVideoFirstFrameFromUrl(item.url)
          .then(setThumbUrl)
          .catch(() => setThumbUrl(item.url));
      } else {
        setThumbUrl(item.url);
      }
    } else if (item.file.type.startsWith('video/')) {
      extractVideoFirstFrame(item.file)
        .then(setThumbUrl)
        .catch(() => setThumbUrl(undefined));
    } else {
      setThumbUrl(createMediaBlobUrl(item.file));
    }
  }, [item]);

  return (
    <div className="relative group/thumb">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onClick}
            className={`relative w-full overflow-hidden rounded-lg bg-slate-100 transition-all ${aspectClass(
              ratio,
            )} ${isActive && isSectionActive ? 'ring-2 ring-[#0014cc]' : 'ring-1 ring-slate-200 hover:ring-slate-300'}`}
          >
            {thumbUrl ? (
              <img
                src={thumbUrl}
                alt={`Asset ${index + 1}`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Upload className="w-3.5 h-3.5 text-slate-400" />
              </div>
            )}
            <div className="absolute top-1 left-1 w-3.5 h-3.5 rounded-full text-[8px] font-medium flex items-center justify-center bg-black/25 text-white">
              {index + 1}
            </div>
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {isActive && isSectionActive
            ? 'Currently showing in mockup'
            : 'Click to preview in mockup'}
        </TooltipContent>
      </Tooltip>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-400 hover:bg-red-600 text-white flex items-center justify-center opacity-0 group-hover/thumb:opacity-100 transition-opacity"
      >
        <X className="w-2.5 h-2.5" />
      </button>
    </div>
  );
}

// ─── Asset accordion — self-managed, supports mixed existing/new items ─────────

function AssetAccordion({
  label,
  tooltip,
  ratio,
  initialItems,
  injectedFiles,
  isSectionActive,
  fieldIssues,
  showErrors,
  showWarnings,
  fieldKey,
  feedback,
  feedbackPending,
  onFeedback,
  onActiveUrlChange,
  onUserSelect,
  onFilesChange,
}: {
  label: string;
  tooltip?: string;
  ratio: DCRatio;
  initialItems?: DCItem[];
  /** Files injected from outside (e.g., GX import). Added to items when changed. */
  injectedFiles?: File[];
  isSectionActive: boolean;
  fieldIssues?: Record<string, FieldIssue>;
  showErrors?: boolean;
  showWarnings?: boolean;
  fieldKey?: string | string[];
  feedback?: Record<string, ValidationFeedback>;
  feedbackPending?: Set<string>;
  onFeedback?: (
    warningKey: string,
    warningMessage: string,
    action: 'accepted' | 'dismissed',
  ) => void;
  onActiveUrlChange: (url: string | undefined) => void;
  /**
   * A thumbnail was clicked, as opposed to the active item changing because the
   * list re-seeded or an item was deleted. The two feed sections share one
   * mockup, so this is what lets a 4:5 click take the mockup over from the 1:1
   * section — the same radio-group behaviour the single-image format has.
   */
  onUserSelect?: (url: string | undefined) => void;
  onFilesChange: (newFiles: File[], deletedIds: string[], order: AssetOrderToken[]) => void;
}) {
  const [items, setItems] = useState<DCItem[]>(initialItems ?? []);
  // True while the parent has assets we haven't taken into `items` yet. Kept in a
  // ref so the notify effect can consult it without depending on `initialItems`.
  const pendingSeedRef = useRef(false);
  pendingSeedRef.current = !!initialItems?.length;
  const [activeIndex, setActiveIndex] = useState(0);
  const [deletedIds, setDeletedIds] = useState<string[]>([]);
  // Signature of the initialItems last seeded from — see seedSignature.ts.
  // null means "never seeded". Must NOT be primed from the first render's props:
  // the parent mounts this with an empty list while resolution is still in flight,
  // and priming from that would record '' as a real seed.
  const seededSigRef = useRef<string | null>(null);
  const [open, setOpen] = useState(true);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Derive validation state for the add button
  const issue = fieldKey ? mergeIssues(fieldKey, fieldIssues ?? {}) : undefined;
  const hasError = showErrors && (issue?.errors?.length ?? 0) > 0;
  const hasWarning = showWarnings && (issue?.warnings?.length ?? 0) > 0;

  const addBtnClass = hasError
    ? 'border-red-400 text-red-500 hover:border-red-500 hover:bg-red-50'
    : hasWarning
      ? 'border-amber-400 text-amber-600 hover:border-amber-500 hover:bg-amber-50'
      : 'border-slate-200 text-slate-400 hover:border-slate-300 hover:bg-slate-50';

  // Re-seed from the DB whenever the incoming asset identities change.
  // Identical guard to the carousel/story components: an empty signature is never
  // seeded from, because the parent publishes [] as a loading placeholder while
  // async URL resolution is in flight. Treating that as a real state is what made
  // assets vanish after Validate until a manual refresh.
  useEffect(() => {
    if (!initialItems) return;
    const sig = seedSignature(initialItems.map(dcSeedKey));
    if (!sig || sig === seededSigRef.current) return;
    seededSigRef.current = sig;
    setItems(initialItems);
    setActiveIndex(0);
    // A re-seed adopts server state wholesale, so deletions staged against the
    // previous seed are already reflected in initialItems.
    setDeletedIds([]);
  }, [initialItems]);

  // Notify parent when the active item URL changes
  useEffect(() => {
    // Don't push "no image" to the mockup while waiting for DB items to seed.
    // Same guard, and for the same reason, as the carousels' notify(): after a
    // save the parent re-resolves asset URLs asynchronously, so this component
    // renders with `items` still empty while `initialItems` already describes
    // real assets. Publishing undefined in that window blanked the mockup until
    // a manual refresh.
    //
    // "Waiting" is `items` being empty while the parent has some, rather than an
    // identity comparison against the mount-time array: the seeding effect calls
    // setItems(initialItems), which keeps the same array identity, so identity
    // would never register the catch-up and the mockup would stay blank forever.
    // A genuinely empty section has no initialItems either, so it still correctly
    // reports undefined.
    //
    // Scoped to before the FIRST seed via seededSigRef, because after one the same
    // "empty here, non-empty in the parent" shape means the opposite thing: the
    // user deleted the last item, and the parent's list is merely stale until the
    // save round-trips. Without that scoping, deleting the last asset silently
    // left the deleted image in the mockup.
    //
    // Read through a ref rather than depending on `initialItems`: a refetch hands
    // us an equal-but-new array every poll, and depending on it would re-run this
    // effect (and re-push the URL) on every one of them.
    if (pendingSeedRef.current && seededSigRef.current === null && items.length === 0) return;
    const item = items[activeIndex];
    if (!item) {
      onActiveUrlChange(undefined);
      return;
    }
    if (item.kind === 'existing') {
      onActiveUrlChange(item.url);
    } else {
      onActiveUrlChange(createMediaBlobUrl(item.file));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, items]);

  function addFiles(incoming: File[]) {
    const toAdd = incoming.slice(0, MAX_ASSETS - items.length);
    if (!toAdd.length) return;
    const newDCItems: DCItem[] = toAdd.map((f) => ({ kind: 'new', file: f }));
    const next = [...items, ...newDCItems];
    const wasEmpty = items.length === 0;
    setItems(next);
    if (wasEmpty) setActiveIndex(0);
    const newFiles = next
      .filter((i): i is DCItem & { kind: 'new' } => i.kind === 'new')
      .map((i) => i.file);
    onFilesChange(newFiles, deletedIds, dcOrderTokens(next));
  }

  // When files are injected from outside (e.g., GX import), add them
  useEffect(() => {
    if (injectedFiles && injectedFiles.length > 0) {
      addFiles(injectedFiles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [injectedFiles]);

  function removeItem(index: number) {
    const item = items[index];
    let newDeletedIds = deletedIds;
    if (item.kind === 'existing') {
      newDeletedIds = [...deletedIds, item.existingAssetId];
      setDeletedIds(newDeletedIds);
    }
    const next = items.filter((_, i) => i !== index);
    const newActive = Math.min(activeIndex, Math.max(0, next.length - 1));
    setItems(next);
    setActiveIndex(newActive);
    const newFiles = next
      .filter((i): i is DCItem & { kind: 'new' } => i.kind === 'new')
      .map((i) => i.file);
    onFilesChange(newFiles, newDeletedIds, dcOrderTokens(next));
  }

  // 4:5 is narrow like 9:16, so it fits the same denser grid.
  const gridCols = ratio === '1x1' ? 'grid-cols-6' : 'grid-cols-7';

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between w-full mb-1.5 group/sec"
      >
        {tooltip ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 group-hover/sec:text-slate-500 transition-colors cursor-default">
                {label}
                <span className="normal-case font-normal tracking-normal ml-1">
                  ({items.length} / {MAX_ASSETS})
                </span>
              </p>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-64">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        ) : (
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 group-hover/sec:text-slate-500 transition-colors">
            {label}
            <span className="normal-case font-normal tracking-normal ml-1">
              ({items.length} / {MAX_ASSETS})
            </span>
          </p>
        )}
        <Chevron
          className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <div className="border-b border-slate-200 mb-3" />

      {open && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDraggingFile(true);
          }}
          onDragLeave={(e) => {
            if (e.currentTarget.contains(e.relatedTarget as Node)) return;
            setIsDraggingFile(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setIsDraggingFile(false);
            addFiles(Array.from(e.dataTransfer.files));
          }}
          className={`grid ${gridCols} gap-2 rounded-xl transition-all ${isDraggingFile ? 'ring-2 ring-[#0014cc]/30 bg-[#0014cc]/[0.02] p-1 -m-1' : ''}`}
        >
          {items.map((item, i) => (
            <AssetThumb
              key={i}
              item={item}
              index={i}
              isActive={i === activeIndex}
              isSectionActive={isSectionActive}
              ratio={ratio}
              onClick={() => {
                setActiveIndex(i);
                onUserSelect?.(item.kind === 'existing' ? item.url : createMediaBlobUrl(item.file));
              }}
              onRemove={() => removeItem(i)}
            />
          ))}

          {items.length < MAX_ASSETS && (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className={`w-full overflow-hidden rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 transition-all ${aspectClass(
                ratio,
              )} ${isDraggingFile ? 'border-[#0014cc]/50 bg-[#0014cc]/5 text-[#0014cc]' : addBtnClass}`}
            >
              <input
                ref={inputRef}
                type="file"
                multiple
                accept="image/*,video/*"
                className="hidden"
                onChange={(e) => {
                  const picked = Array.from(e.target.files ?? []);
                  if (picked.length) addFiles(picked);
                  e.target.value = '';
                }}
              />
              <Plus className="w-4 h-4" />
              <span className="text-[9px] font-medium">{isDraggingFile ? 'Drop' : 'Add'}</span>
            </button>
          )}
        </div>
      )}

      {/* Validation messages below the grid */}
      <InlineIssue
        issue={issue}
        showErrors={!!showErrors}
        showWarnings={!!showWarnings}
        feedback={feedback}
        feedbackPending={feedbackPending}
        onFeedback={onFeedback}
      />
    </div>
  );
}

// ─── Text variation row ───────────────────────────────────────────────────────

function TextRow({
  value,
  isActive,
  limit,
  placeholder,
  multiline,
  onChange,
  onSetActive,
  onRemove,
  rowIssue,
  showErrors,
  showWarnings,
  feedback,
  feedbackPending,
  onFeedback,
}: {
  value: string;
  isActive: boolean;
  limit: number;
  placeholder: string;
  multiline?: boolean;
  onChange: (v: string) => void;
  onSetActive: () => void;
  onRemove?: () => void;
  rowIssue?: FieldIssue;
  showErrors?: boolean;
  showWarnings?: boolean;
  feedback?: Record<string, ValidationFeedback>;
  feedbackPending?: Set<string>;
  onFeedback?: (
    warningKey: string,
    warningMessage: string,
    action: 'accepted' | 'dismissed',
  ) => void;
}) {
  const hasRowIssues =
    (showErrors && (rowIssue?.errors?.length ?? 0) > 0) ||
    (showWarnings && (rowIssue?.warnings?.length ?? 0) > 0);

  return (
    <div
      className={`group rounded-lg border transition-all ${
        isActive ? 'border-slate-200 bg-slate-50' : 'border-slate-100 hover:border-slate-200'
      }`}
      onClick={onSetActive}
    >
      <div className="flex items-start gap-2 p-2">
        <div
          className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 transition-all ${
            isActive ? 'bg-slate-500' : 'bg-slate-200'
          }`}
        />

        <div className="flex-1 min-w-0">
          {multiline ? (
            <textarea
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onFocus={onSetActive}
              placeholder={placeholder}
              rows={2}
              className="w-full text-xs bg-transparent border-none outline-none resize-none text-slate-700 placeholder:text-slate-400 leading-relaxed"
            />
          ) : (
            <input
              type="text"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onFocus={onSetActive}
              placeholder={placeholder}
              className="w-full text-xs bg-transparent border-none outline-none text-slate-700 placeholder:text-slate-400"
            />
          )}
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
          <span
            className={`text-[10px] tabular-nums ${value.length > limit ? 'text-amber-600 font-medium' : 'text-slate-400'}`}
          >
            {value.length}/{limit}
          </span>
          {onRemove && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove();
                  }}
                  className="text-red-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="left">Remove variation</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Per-row inline validation messages */}
      {hasRowIssues && (
        <div className="px-2 pb-2">
          <InlineIssue
            issue={rowIssue}
            showErrors={!!showErrors}
            showWarnings={!!showWarnings}
            inline
            feedback={feedback}
            feedbackPending={feedbackPending}
            onFeedback={onFeedback}
          />
        </div>
      )}
    </div>
  );
}

// ─── Text accordion ───────────────────────────────────────────────────────────

function TextAccordion({
  label,
  tooltip,
  values,
  activeIndex,
  limit,
  placeholder,
  multiline,
  fieldIssues,
  showErrors,
  showWarnings,
  fieldKey,
  feedback,
  feedbackPending,
  onFeedback,
  onValuesChange,
  onActiveChange,
}: {
  label: string;
  tooltip?: string;
  values: string[];
  activeIndex: number;
  limit: number;
  placeholder: string;
  multiline?: boolean;
  fieldIssues?: Record<string, FieldIssue>;
  showErrors?: boolean;
  showWarnings?: boolean;
  fieldKey?: string;
  feedback?: Record<string, ValidationFeedback>;
  feedbackPending?: Set<string>;
  onFeedback?: (
    warningKey: string,
    warningMessage: string,
    action: 'accepted' | 'dismissed',
  ) => void;
  onValuesChange: (values: string[], newActiveIndex?: number) => void;
  onActiveChange: (index: number) => void;
}) {
  const [open, setOpen] = useState(true);

  const issue = fieldKey ? fieldIssues?.[fieldKey] : undefined;
  const hasError = showErrors && (issue?.errors?.length ?? 0) > 0;
  const hasWarning = showWarnings && (issue?.warnings?.length ?? 0) > 0;

  const addBtnClass = hasError
    ? 'border-red-400 text-red-500 hover:border-red-500 hover:bg-red-50'
    : hasWarning
      ? 'border-amber-400 text-amber-600 hover:border-amber-500 hover:bg-amber-50'
      : 'border-slate-200 text-slate-400 hover:border-slate-300 hover:bg-slate-50';

  function updateValue(index: number, v: string) {
    const next = [...values];
    next[index] = v;
    onValuesChange(next);
  }

  function addVariation() {
    if (values.length >= MAX_VARIATIONS) return;
    onValuesChange([...values, '']);
  }

  function removeVariation(index: number) {
    if (values.length <= 1) return;
    const next = values.filter((_, i) => i !== index);
    const newActive = index <= activeIndex ? Math.max(0, activeIndex - 1) : activeIndex;
    onValuesChange(next, newActive);
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between w-full mb-1.5 group/sec"
      >
        {tooltip ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 group-hover/sec:text-slate-500 transition-colors cursor-default">
                {label}
                <span className="normal-case font-normal tracking-normal ml-1">
                  ({values.length} / {MAX_VARIATIONS})
                </span>
              </p>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-64">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        ) : (
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 group-hover/sec:text-slate-500 transition-colors">
            {label}
            <span className="normal-case font-normal tracking-normal ml-1">
              ({values.length} / {MAX_VARIATIONS})
            </span>
          </p>
        )}
        <Chevron
          className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <div className="border-b border-slate-200 mb-3" />

      {open && (
        <div className="space-y-1.5">
          {values.map((v, i) => {
            const rowIssue = fieldKey ? fieldIssues?.[`${fieldKey}.${i}`] : undefined;
            return (
              <TextRow
                key={i}
                value={v}
                isActive={i === activeIndex}
                limit={limit}
                placeholder={`${placeholder}${values.length > 1 ? ` ${i + 1}` : ''}...`}
                multiline={multiline}
                onChange={(text) => updateValue(i, text)}
                onSetActive={() => onActiveChange(i)}
                onRemove={values.length > 1 ? () => removeVariation(i) : undefined}
                rowIssue={rowIssue}
                showErrors={showErrors}
                showWarnings={showWarnings}
                feedback={feedback}
                feedbackPending={feedbackPending}
                onFeedback={onFeedback}
              />
            );
          })}
          {values.length < MAX_VARIATIONS && (
            <button
              type="button"
              onClick={addVariation}
              className={`w-full py-2 rounded-lg border-2 border-dashed flex items-center justify-center gap-1.5 transition-all text-xs font-medium ${addBtnClass}`}
            >
              <Plus className="w-3.5 h-3.5" />
              Add variation
            </button>
          )}
        </div>
      )}

      {/* Validation messages */}
      <InlineIssue
        issue={issue}
        showErrors={!!showErrors}
        showWarnings={!!showWarnings}
        feedback={feedback}
        feedbackPending={feedbackPending}
        onFeedback={onFeedback}
      />
    </div>
  );
}

// ─── Select accordion (for CTA dropdowns) ────────────────────────────────────

function SelectAccordion({
  label,
  tooltip,
  values,
  activeIndex,
  options,
  fieldIssues,
  showErrors,
  showWarnings,
  fieldKey,
  feedback,
  feedbackPending,
  onFeedback,
  onValuesChange,
  onActiveChange,
}: {
  label: string;
  tooltip?: string;
  values: string[];
  activeIndex: number;
  options: string[];
  fieldIssues?: Record<string, FieldIssue>;
  showErrors?: boolean;
  showWarnings?: boolean;
  fieldKey?: string;
  feedback?: Record<string, ValidationFeedback>;
  feedbackPending?: Set<string>;
  onFeedback?: (
    warningKey: string,
    warningMessage: string,
    action: 'accepted' | 'dismissed',
  ) => void;
  onValuesChange: (values: string[], newActiveIndex?: number) => void;
  onActiveChange: (index: number) => void;
}) {
  const [open, setOpen] = useState(true);

  const issue = fieldKey ? fieldIssues?.[fieldKey] : undefined;
  const hasError = showErrors && (issue?.errors?.length ?? 0) > 0;
  const hasWarning = showWarnings && (issue?.warnings?.length ?? 0) > 0;

  const addBtnClass = hasError
    ? 'border-red-400 text-red-500 hover:border-red-500 hover:bg-red-50'
    : hasWarning
      ? 'border-amber-400 text-amber-600 hover:border-amber-500 hover:bg-amber-50'
      : 'border-slate-200 text-slate-400 hover:border-slate-300 hover:bg-slate-50';

  function updateValue(index: number, v: string) {
    const next = [...values];
    next[index] = v;
    onValuesChange(next);
  }

  function addVariation() {
    if (values.length >= MAX_VARIATIONS) return;
    onValuesChange([...values, options[0] ?? '']);
  }

  function removeVariation(index: number) {
    if (values.length <= 1) return;
    const next = values.filter((_, i) => i !== index);
    const newActive = index <= activeIndex ? Math.max(0, activeIndex - 1) : activeIndex;
    onValuesChange(next, newActive);
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between w-full mb-1.5 group/sec"
      >
        {tooltip ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 group-hover/sec:text-slate-500 transition-colors cursor-default">
                {label}
                <span className="normal-case font-normal tracking-normal ml-1">
                  ({values.length} / {MAX_VARIATIONS})
                </span>
              </p>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-64">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        ) : (
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 group-hover/sec:text-slate-500 transition-colors">
            {label}
            <span className="normal-case font-normal tracking-normal ml-1">
              ({values.length} / {MAX_VARIATIONS})
            </span>
          </p>
        )}
        <Chevron
          className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <div className="border-b border-slate-200 mb-3" />

      {open && (
        <div className="space-y-1.5">
          {values.map((v, i) => (
            <div
              key={i}
              className={`flex items-center gap-1.5 rounded-lg border px-2 py-1.5 transition-all cursor-pointer ${
                i === activeIndex
                  ? 'border-[#0014cc]/40 bg-[#0014cc]/[0.03] ring-1 ring-[#0014cc]/20'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => onActiveChange(i)}
            >
              <select
                value={v}
                onChange={(e) => updateValue(i, e.target.value)}
                className="flex-1 bg-transparent text-sm text-slate-700 outline-none cursor-pointer"
              >
                {options.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
              {values.length > 1 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeVariation(i);
                  }}
                  className="flex-shrink-0 p-0.5 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
          {values.length < MAX_VARIATIONS && (
            <button
              type="button"
              onClick={addVariation}
              className={`w-full py-2 rounded-lg border-2 border-dashed flex items-center justify-center gap-1.5 transition-all text-xs font-medium ${addBtnClass}`}
            >
              <Plus className="w-3.5 h-3.5" />
              Add variation
            </button>
          )}
        </div>
      )}

      <InlineIssue
        issue={issue}
        showErrors={!!showErrors}
        showWarnings={!!showWarnings}
        feedback={feedback}
        feedbackPending={feedbackPending}
        onFeedback={onFeedback}
      />
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export interface MetaDynamicCreativeDetailsProps {
  campaignId?: string;
  fordshareIds?: string[] | null;
  campaign?: Campaign | null;
  showErrors: boolean;
  showWarnings: boolean;
  fieldIssues: Record<string, FieldIssue>;
  activeRatio: '1x1' | '9x16' | 'reel';
  initialValues?: MetaDCInitialValues;
  feedback?: Record<string, ValidationFeedback>;
  feedbackPending?: Set<string>;
  onAIFeedback?: (
    warningKey: string,
    warningMessage: string,
    action: 'accepted' | 'dismissed',
  ) => void;
  // Mockup preview callbacks (active values only)
  onImageUrl1x1Change: (url: string | undefined) => void;
  onImageUrl9x16Change: (url: string | undefined) => void;
  onCaptionChange: (v: string) => void;
  onHeadlineChange: (v: string) => void;
  onDescriptionChange: (v: string) => void;
  onCtaChange: (v: string) => void;
  // Save callbacks (full arrays / all fields)
  onPrimaryTextsChange?: (texts: string[]) => void;
  onHeadlinesArrayChange?: (texts: string[]) => void;
  onDescriptionsArrayChange?: (texts: string[]) => void;
  onCtasArrayChange?: (ctas: string[]) => void;
  onUrlChange?: (url: string) => void;
  onLegalChange?: (legal: string) => void;
  onNewFiles1x1Change?: (newFiles: File[], deletedIds: string[], order: AssetOrderToken[]) => void;
  onNewFiles4x5Change?: (newFiles: File[], deletedIds: string[], order: AssetOrderToken[]) => void;
  onNewFiles9x16Change?: (newFiles: File[], deletedIds: string[], order: AssetOrderToken[]) => void;
  usedInVariants?: Map<string, number[]>;
  gxData?: import('@/api/gearExchange').GXMultiSearchResponse | null;
  gxLoading?: boolean;
  gxError?: boolean;
}

export function MetaDynamicCreativeDetails({
  campaignId,
  fordshareIds,
  campaign,
  showErrors,
  showWarnings,
  fieldIssues,
  activeRatio,
  initialValues,
  feedback,
  feedbackPending,
  onAIFeedback,
  onImageUrl1x1Change,
  onImageUrl9x16Change,
  onCaptionChange,
  onHeadlineChange,
  onDescriptionChange,
  onCtaChange,
  onPrimaryTextsChange,
  onHeadlinesArrayChange,
  onDescriptionsArrayChange,
  onCtasArrayChange,
  onUrlChange,
  onLegalChange,
  onNewFiles1x1Change,
  onNewFiles4x5Change,
  onNewFiles9x16Change,
  usedInVariants,
  gxData,
  gxLoading,
  gxError,
}: MetaDynamicCreativeDetailsProps) {
  const [primaryTexts, setPrimaryTexts] = useState<string[]>(
    initialValues?.primaryTexts?.length ? initialValues.primaryTexts : [''],
  );
  const [activePrimary, setActivePrimary] = useState(0);
  const [headlines, setHeadlines] = useState<string[]>(
    initialValues?.headlines?.length ? initialValues.headlines : [''],
  );
  const [activeHeadline, setActiveHeadline] = useState(0);
  const [descriptions, setDescriptions] = useState<string[]>(
    initialValues?.descriptions?.length ? initialValues.descriptions : [''],
  );
  const [activeDescription, setActiveDescription] = useState(0);
  const [ctas, setCtas] = useState<string[]>(() => {
    const arr = normalizeCta(initialValues?.cta);
    return arr.length ? arr : ['Learn More'];
  });
  const [activeCta, setActiveCta] = useState(0);
  const [destinationUrl, setDestinationUrl] = useState(initialValues?.url ?? '');
  const [legal, setLegal] = useState(initialValues?.legal ?? '');

  // Gear Exchange state
  const [showGxModal, setShowGxModal] = useState(false);
  const [gxInjected1x1, setGxInjected1x1] = useState<File[]>();
  const [gxInjected4x5, setGxInjected4x5] = useState<File[]>();
  const [gxInjected9x16, setGxInjected9x16] = useState<File[]>();
  // Track actual item counts in each accordion (API items - deleted + new files).
  // Updated via onFilesChange which fires on every add/remove.
  const [itemCount1x1, setItemCount1x1] = useState(initialValues?.initial1x1?.length ?? 0);
  const [itemCount4x5, setItemCount4x5] = useState(initialValues?.initial4x5?.length ?? 0);
  const [itemCount9x16, setItemCount9x16] = useState(initialValues?.initial9x16?.length ?? 0);
  // Which of the two feed sections currently owns the shared feed mockup. 1:1 is
  // the primary crop so it starts there, but clicking a 4:5 thumbnail hands it
  // over — otherwise there is no way to preview a 4:5 at all.
  const [activeFeedSlot, setActiveFeedSlot] = useState<'1x1' | '4x5'>('1x1');
  // The 1:1 section's current URL, recorded even while the 4:5 section owns the
  // mockup. Deleting the last 4:5 has to hand the mockup back, and the 1:1
  // section won't re-announce itself just because ownership moved.
  const lastUrl1x1Ref = useRef<string | undefined>(undefined);

  // On mount: sync initial state up to parent so isDirty and save payload are correct
  useEffect(() => {
    onPrimaryTextsChange?.(primaryTexts);
    onHeadlinesArrayChange?.(headlines);
    onDescriptionsArrayChange?.(descriptions);
    onUrlChange?.(destinationUrl);
    onLegalChange?.(legal);
    onCtaChange(ctas[activeCta] ?? 'Learn More');
    onCtasArrayChange?.(ctas);
    onCaptionChange(primaryTexts[activePrimary] ?? '');
    onHeadlineChange(headlines[activeHeadline] ?? '');
    onDescriptionChange(descriptions[activeDescription] ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Text handlers ──────────────────────────────────────────────────────────

  function handlePrimaryTextsChange(next: string[], newActive?: number) {
    const idx = newActive ?? activePrimary;
    setPrimaryTexts(next);
    setActivePrimary(idx);
    onCaptionChange(next[idx] ?? '');
    onPrimaryTextsChange?.(next);
  }

  function handleActivePrimaryChange(index: number) {
    setActivePrimary(index);
    onCaptionChange(primaryTexts[index] ?? '');
  }

  function handleHeadlinesChange(next: string[], newActive?: number) {
    const idx = newActive ?? activeHeadline;
    setHeadlines(next);
    setActiveHeadline(idx);
    onHeadlineChange(next[idx] ?? '');
    onHeadlinesArrayChange?.(next);
  }

  function handleActiveHeadlineChange(index: number) {
    setActiveHeadline(index);
    onHeadlineChange(headlines[index] ?? '');
  }

  function handleDescriptionsChange(next: string[], newActive?: number) {
    const idx = newActive ?? activeDescription;
    setDescriptions(next);
    setActiveDescription(idx);
    onDescriptionChange(next[idx] ?? '');
    onDescriptionsArrayChange?.(next);
  }

  function handleActiveDescriptionChange(index: number) {
    setActiveDescription(index);
    onDescriptionChange(descriptions[index] ?? '');
  }

  function handleCtasChange(next: string[], newActive?: number) {
    const idx = newActive ?? activeCta;
    setCtas(next);
    setActiveCta(idx);
    onCtaChange(next[idx] ?? 'Learn More');
    onCtasArrayChange?.(next);
  }

  function handleActiveCtaChange(index: number) {
    setActiveCta(index);
    onCtaChange(ctas[index] ?? 'Learn More');
  }

  function handleUrlChange(value: string) {
    setDestinationUrl(value);
    onUrlChange?.(value);
  }

  function handleLegalChange(value: string) {
    setLegal(value);
    onLegalChange?.(value);
  }

  const urlIssue = fieldIssues['destination_url'];

  return (
    <div className="flex-1 overflow-y-auto hover-scroll px-6 py-5 space-y-6 min-h-0">
      {/* ── About ───────────────────────────────────────────────────── */}
      <div className="border-l-2 border-[#0014cc]/30 pl-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
          About this format
        </p>
        <p className="text-sm text-slate-600 leading-relaxed">
          Upload multiple assets and copy variations — Meta automatically mixes and matches every
          combination, then serves whichever performs best to each person. Provide up to{' '}
          <span className="font-medium text-slate-700">10 images or videos</span> per ratio and up
          to <span className="font-medium text-slate-700">5 variations</span> of each text field.{' '}
          <a
            href="https://www.facebook.com/business/help/170372403538781?id=244556379685063"
            target="_blank"
            rel="noopener noreferrer"
            className="text-slate-400 hover:text-slate-600 underline underline-offset-2 decoration-slate-300"
          >
            Meta Dynamic Creative specs ↗
          </a>
        </p>
      </div>

      {/* ── Gear Exchange Import ──────────────────────────────────── */}
      {
        <>
          <GearExchangeImportButton
            totalAssets={gxData?.total_assets ?? null}
            loading={gxLoading}
            error={gxError}
            fordshareIds={fordshareIds ?? undefined}
            onClick={() => setShowGxModal(true)}
          />

          {showGxModal && gxData && campaign && campaignId && (
            <GearExchangeModal
              data={gxData}
              campaign={campaign}
              campaignId={campaignId}
              formatLabel="Meta Dynamic Creative"
              zoneLimits={[
                { ratio: '1x1', limit: 10, existing: itemCount1x1 },
                { ratio: '4x5', limit: 10, existing: itemCount4x5 },
                { ratio: '9x16', limit: 10, existing: itemCount9x16 },
              ]}
              onClose={() => setShowGxModal(false)}
              usedInVariants={usedInVariants}
              onImport={(files: GXImportedFile[]) => {
                const files1x1 = files.filter((f) => f.ratio === '1x1').map((f) => f.file);
                const files4x5 = files.filter((f) => f.ratio === '4x5').map((f) => f.file);
                const files9x16 = files.filter((f) => f.ratio === '9x16').map((f) => f.file);
                if (files1x1.length > 0) setGxInjected1x1(files1x1);
                if (files4x5.length > 0) setGxInjected4x5(files4x5);
                if (files9x16.length > 0) setGxInjected9x16(files9x16);
              }}
            />
          )}
        </>
      }

      {/* ── 1:1 Feed Assets ───────────────────────────────────────────────── */}
      <AssetAccordion
        label="Feed Assets · 1:1"
        tooltip="Square images or videos shown in Facebook and Instagram feeds. Upload up to 10 — Meta tests every asset against every copy variation and serves the best combination."
        ratio="1x1"
        initialItems={initialValues?.initial1x1}
        injectedFiles={gxInjected1x1}
        isSectionActive={activeRatio === '1x1' && activeFeedSlot === '1x1'}
        fieldIssues={fieldIssues}
        showErrors={showErrors}
        showWarnings={showWarnings}
        // Two keys: `feed` is the shared "the placement has too few images"
        // warning, which either crop fills, so it counts both pools and surfaces
        // here because 1:1 is the primary crop. `1x1` carries the warnings about
        // this pool specifically — without it they only reached the summary
        // bubble and never appeared under the section they refer to.
        fieldKey={['ratio_items.feed', 'ratio_items.1x1']}
        feedback={feedback}
        feedbackPending={feedbackPending}
        onFeedback={onAIFeedback}
        onActiveUrlChange={(url) => {
          // Record it either way: if the 4:5 section later empties, this is what
          // the mockup falls back to.
          lastUrl1x1Ref.current = url;
          // Don't yank the mockup back off a 4:5 the user picked. Re-seeds and
          // deletions fire this too, not just clicks.
          if (activeFeedSlot === '1x1') onImageUrl1x1Change(url);
        }}
        onUserSelect={(url) => {
          setActiveFeedSlot('1x1');
          onImageUrl1x1Change(url);
        }}
        onFilesChange={(newFiles, deletedIds, order) => {
          const apiRemaining = (initialValues?.initial1x1?.length ?? 0) - deletedIds.length;
          setItemCount1x1(apiRemaining + newFiles.length);
          onNewFiles1x1Change?.(newFiles, deletedIds, order);
        }}
      />

      {/* ── 4:5 Feed Assets ───────────────────────────────────────────────── */}
      {/*
        Its own section, not a second slot on a 1:1 card: DC has no cards. Meta
        draws from a flat pool per ratio, so a 4:5 here is an alternative asset
        Meta may serve, not a crop of a specific 1:1 one.
      */}
      <AssetAccordion
        label="Feed Assets · 4:5"
        tooltip="Portrait (4:5) images or videos for Facebook and Instagram feeds. Optional — Meta falls back to your 1:1 assets where no 4:5 is provided. Upload up to 10."
        ratio="4x5"
        initialItems={initialValues?.initial4x5}
        injectedFiles={gxInjected4x5}
        isSectionActive={activeRatio === '1x1' && activeFeedSlot === '4x5'}
        fieldIssues={fieldIssues}
        showErrors={showErrors}
        showWarnings={showWarnings}
        fieldKey="ratio_items.4x5"
        feedback={feedback}
        feedbackPending={feedbackPending}
        onFeedback={onAIFeedback}
        onActiveUrlChange={(url) => {
          // Take the shared mockup only when this section owns it, or when there
          // is no 1:1 at all to show — a 4:5-only campaign must still preview.
          if (activeFeedSlot === '4x5' || !initialValues?.initial1x1?.length) {
            // An undefined url means this section just emptied (its last item was
            // deleted). Don't publish it: that would blank a mockup the 1:1
            // section could still fill. Hand ownership back instead, and fall
            // back to undefined only when 1:1 has nothing either.
            if (url) {
              onImageUrl1x1Change(url);
            } else {
              setActiveFeedSlot('1x1');
              onImageUrl1x1Change(lastUrl1x1Ref.current);
            }
          }
        }}
        onUserSelect={(url) => {
          setActiveFeedSlot('4x5');
          onImageUrl1x1Change(url);
        }}
        onFilesChange={(newFiles, deletedIds, order) => {
          const apiRemaining = (initialValues?.initial4x5?.length ?? 0) - deletedIds.length;
          setItemCount4x5(apiRemaining + newFiles.length);
          onNewFiles4x5Change?.(newFiles, deletedIds, order);
        }}
      />

      {/* ── 9:16 Assets ─────────────────────────────────────────────── */}
      <AssetAccordion
        label="Story & Reel Assets · 9:16"
        tooltip="Vertical full-screen images or videos shown in Instagram Stories and Reels. Upload up to 10 — Meta tests all combinations automatically."
        ratio="9x16"
        initialItems={initialValues?.initial9x16}
        injectedFiles={gxInjected9x16}
        isSectionActive={activeRatio === '9x16' || activeRatio === 'reel'}
        fieldIssues={fieldIssues}
        showErrors={showErrors}
        showWarnings={showWarnings}
        fieldKey="ratio_items.9x16"
        feedback={feedback}
        feedbackPending={feedbackPending}
        onFeedback={onAIFeedback}
        onActiveUrlChange={(url) => {
          onImageUrl9x16Change(url);
        }}
        onFilesChange={(newFiles, deletedIds, order) => {
          const apiRemaining = (initialValues?.initial9x16?.length ?? 0) - deletedIds.length;
          setItemCount9x16(apiRemaining + newFiles.length);
          onNewFiles9x16Change?.(newFiles, deletedIds, order);
        }}
      />

      {/* ── Primary Text ────────────────────────────────────────────── */}
      <TextAccordion
        label="Primary Text"
        tooltip="The main copy shown above the ad. Add up to 5 variations — Meta tests each one and automatically serves whichever performs best."
        values={primaryTexts}
        activeIndex={activePrimary}
        limit={PRIMARY_TEXT_LIMIT}
        placeholder="Write your primary ad copy"
        multiline
        fieldIssues={fieldIssues}
        showErrors={showErrors}
        showWarnings={showWarnings}
        fieldKey="primary_text"
        feedback={feedback}
        feedbackPending={feedbackPending}
        onFeedback={onAIFeedback}
        onValuesChange={handlePrimaryTextsChange}
        onActiveChange={handleActivePrimaryChange}
      />

      {/* ── Headlines ───────────────────────────────────────────────── */}
      <TextAccordion
        label="Headlines"
        tooltip="Bold text shown below the image in the link preview. Up to 40 characters and 5 variations — Meta tests and serves the best performer."
        values={headlines}
        activeIndex={activeHeadline}
        limit={HEADLINE_LIMIT}
        placeholder="Short, punchy headline"
        fieldIssues={fieldIssues}
        showErrors={showErrors}
        showWarnings={showWarnings}
        fieldKey="headline"
        feedback={feedback}
        feedbackPending={feedbackPending}
        onFeedback={onAIFeedback}
        onValuesChange={handleHeadlinesChange}
        onActiveChange={handleActiveHeadlineChange}
      />

      {/* ── Descriptions ────────────────────────────────────────────── */}
      <TextAccordion
        label="Descriptions"
        tooltip="Optional supporting line beneath the headline. Not always shown by Meta — up to 25 characters and 5 variations."
        values={descriptions}
        activeIndex={activeDescription}
        limit={DESCRIPTION_LIMIT}
        placeholder="Short supporting description"
        fieldIssues={fieldIssues}
        showErrors={showErrors}
        showWarnings={showWarnings}
        fieldKey="description"
        feedback={feedback}
        feedbackPending={feedbackPending}
        onFeedback={onAIFeedback}
        onValuesChange={handleDescriptionsChange}
        onActiveChange={handleActiveDescriptionChange}
      />

      {/* ── Call to Action ────────────────────────────────────────── */}
      <SelectAccordion
        label="Call to Action"
        tooltip="The button label shown on the ad. Up to 5 variations — Meta tests and serves the best performer."
        values={ctas}
        activeIndex={activeCta}
        options={CTA_OPTIONS}
        fieldIssues={fieldIssues}
        showErrors={showErrors}
        showWarnings={showWarnings}
        fieldKey="cta"
        feedback={feedback}
        feedbackPending={feedbackPending}
        onFeedback={onAIFeedback}
        onValuesChange={handleCtasChange}
        onActiveChange={handleActiveCtaChange}
      />

      {/* ── Destination URL ───────────────────────────────────────── */}
      <div>
        <FieldLabel
          label="Destination URL"
          tooltip="Shared across all asset and copy combinations. Make sure it matches the offer in your creative."
        />
        <input
          type="url"
          value={destinationUrl}
          onChange={(e) => handleUrlChange(e.target.value)}
          placeholder="https://www.ford.com/..."
          className={`${inputClass} ${showErrors && (urlIssue?.errors?.length ?? 0) > 0 ? 'border-red-400 focus:border-red-400' : ''}`}
        />
        <InlineIssue
          issue={urlIssue}
          showErrors={showErrors}
          showWarnings={showWarnings}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onFeedback={onAIFeedback}
        />
      </div>

      {/* ── Legal ───────────────────────────────────────────────────── */}
      <Field
        label="Legal"
        tooltip="Required in some markets (e.g. France) for regulated categories. Internal only — not displayed in the mockup."
        fieldKey="legal"
        fieldIssues={fieldIssues}
        showErrors={showErrors}
        showWarnings={showWarnings}
        feedback={feedback}
        feedbackPending={feedbackPending}
        onFeedback={onAIFeedback}
        help={<>Enter "N/A" if your market does not require a legal disclaimer.</>}
      >
        <textarea
          value={legal}
          onChange={(e) => handleLegalChange(e.target.value)}
          placeholder="Legal disclaimer — required for some markets (e.g. FR)..."
          rows={5}
          className={`${inputClass} resize-none ${issueBorder(fieldIssues['legal'], showErrors, showWarnings)}`}
        />
      </Field>
    </div>
  );
}
